import React from "react";
import { MapPin, Navigation, Copy } from "lucide-react";
import { Reveal, Overline } from "@/components/site/Reveal";
import { toast } from "sonner";

const ADDRESS = "Rua José Alexandre Rocha, 63 — Perequê, Porto Belo/SC";
const ADDRESS_SHORT = "R. José Alexandre Rocha, 63";
const NEIGHBORHOOD = "Perequê — Porto Belo/SC";
const MAPS_QUERY = "Rua+José+Alexandre+Rocha,+63,+Perequê,+Porto+Belo,+SC";
const EMBED_URL = `https://www.google.com/maps?q=${MAPS_QUERY}&hl=pt-BR&z=16&output=embed`;
const NAV_URL = `https://www.google.com/maps/dir/?api=1&destination=${MAPS_QUERY}`;

export default function MapSection() {
  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(ADDRESS);
      toast.success("Endereço copiado!");
    } catch {
      toast.error("Não foi possível copiar. Tente novamente.");
    }
  };

  return (
    <section className="relative bg-white py-20 md:py-24" data-testid="map-section">
      <div className="mx-auto max-w-6xl px-6 lg:px-8">
        <Reveal className="mx-auto max-w-2xl text-center">
          <Overline className="text-brand-red">Como chegar</Overline>
          <h2 className="mt-4 font-display text-4xl font-extrabold uppercase leading-[1.04] tracking-tight text-brand-navy sm:text-5xl">
            Onde fica a Dive Master
          </h2>
          <p className="mt-4 text-base text-slate-600 sm:text-lg">
            Estamos em <strong className="text-brand-navy">Porto Belo — Santa Catarina</strong>,
            com estacionamento fácil e a poucos minutos do centro.
          </p>
        </Reveal>

        <Reveal className="mt-12">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* Map iframe */}
            <div className="relative overflow-hidden rounded-3xl shadow-2xl shadow-ocean-deep/15 ring-1 ring-ocean-mid/10 lg:col-span-2">
              <div className="aspect-[16/12] w-full sm:aspect-[16/10] lg:aspect-[4/3]">
                <iframe
                  title="Localização da Dive Master no Google Maps"
                  src={EMBED_URL}
                  className="h-full w-full border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                  data-testid="map-iframe"
                />
              </div>
            </div>

            {/* Info card */}
            <div className="flex flex-col justify-between rounded-3xl bg-gradient-to-br from-brand-navy to-ocean-deep p-8 text-white shadow-2xl shadow-ocean-deep/25">
              <div>
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-red text-white shadow-lg shadow-brand-red/40">
                  <MapPin className="h-6 w-6" />
                </div>
                <p className="mt-6 text-xs font-bold uppercase tracking-[0.24em] text-ocean-glow">
                  Nosso endereço
                </p>
                <h3 className="mt-2 font-display text-3xl font-black leading-tight">
                  {ADDRESS_SHORT}
                </h3>
                <p className="mt-1 text-base text-white/80">{NEIGHBORHOOD}</p>
              </div>

              <div className="mt-8 flex flex-col gap-3">
                <a
                  href={NAV_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-red px-6 py-3 text-sm font-extrabold uppercase tracking-wide text-white shadow-lg shadow-brand-red/30 transition-transform hover:scale-[1.02]"
                  data-testid="map-navigate"
                >
                  <Navigation className="h-4 w-4" /> Traçar rota
                </a>
                <button
                  onClick={copyAddress}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-white/25 bg-white/5 px-6 py-3 text-sm font-bold uppercase tracking-wide text-white transition-colors hover:bg-white/10"
                  data-testid="map-copy"
                >
                  <Copy className="h-4 w-4" /> Copiar endereço
                </button>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
